import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { downloadBulkTemplate, bulkUploadTasks, quickCreateTasks, getUsers, getDepartmentCategories, getSubcategories } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Download, Plus, Trash2, Loader2, FileSpreadsheet, Table2 } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';

const BulkTaskPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const isAllowed = user?.role === 'Admin' || user?.role === 'Coordinator' || isSuperAdmin;
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const defaultDept = isSuperAdmin ? 'Marketing' : (user?.department || 'Marketing');
  const [rows, setRows] = useState([{ department: defaultDept, task_name: '', due_date: '', priority: 5, assigned_to: '', category: '', sub_category: '' }]);
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [categoriesByDept, setCategoriesByDept] = useState({}); // { dept: [name, ...] }
  const [subsByDeptCat, setSubsByDeptCat] = useState({}); // { 'dept|cat': [{id,name}, ...] }

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const params = {};
        if (!isSuperAdmin) params.department = user?.department;
        const res = await getUsers(params);
        setTeamMembers(res.data.filter(u => u.role === 'Coordinator' || u.role === 'Member'));
      } catch { setTeamMembers([]); }
    };
    fetchMembers();
  }, [isSuperAdmin, user?.department]);

  const loadCategoriesForDept = useCallback(async (dept) => {
    if (!dept || categoriesByDept[dept]) return;
    try {
      const res = await getDepartmentCategories(dept);
      setCategoriesByDept(prev => ({ ...prev, [dept]: res.data.categories || [] }));
    } catch {
      setCategoriesByDept(prev => ({ ...prev, [dept]: [] }));
    }
  }, [categoriesByDept]);

  const loadSubsForCat = useCallback(async (dept, cat) => {
    const key = `${dept}|${cat}`;
    if (!dept || !cat || subsByDeptCat[key]) return;
    try {
      const res = await getSubcategories({ department: dept, category: cat });
      setSubsByDeptCat(prev => ({ ...prev, [key]: res.data || [] }));
    } catch {
      setSubsByDeptCat(prev => ({ ...prev, [key]: [] }));
    }
  }, [subsByDeptCat]);

  // Pre-load defaults
  useEffect(() => { loadCategoriesForDept(defaultDept); }, [defaultDept, loadCategoriesForDept]);

  const handleTemplate = async () => {
    try {
      const res = await downloadBulkTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Bulk_Task_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const res = await bulkUploadTasks(file);
      setUploadResult(res.data);
      if (res.data.created > 0) toast.success(`${res.data.created} tasks created successfully`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} rows had errors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const addRow = () => setRows([...rows, { department: defaultDept, task_name: '', due_date: '', priority: 5, assigned_to: '', category: '', sub_category: '' }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const updated = [...rows];
    updated[i][field] = value;
    // Cascade resets
    if (field === 'department') {
      updated[i].category = '';
      updated[i].sub_category = '';
      updated[i].assigned_to = '';
      loadCategoriesForDept(value);
    } else if (field === 'category') {
      updated[i].sub_category = '';
      if (value) loadSubsForCat(updated[i].department, value);
    }
    setRows(updated);
  };

  const handleQuickSubmit = async () => {
    const valid = rows.filter(r => r.task_name && r.due_date && r.assigned_to && r.department);
    if (!valid.length) return toast.error('Fill at least one complete row (Department, Task Name, Due Date, Assigned To required)');
    setQuickSubmitting(true);
    try {
      const res = await quickCreateTasks(valid);
      if (res.data.created > 0) {
        toast.success(`${res.data.created} tasks created`);
        setRows([{ department: defaultDept, task_name: '', due_date: '', priority: 5, assigned_to: '', category: '', sub_category: '' }]);
      }
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} rows failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tasks');
    } finally { setQuickSubmitting(false); }
  };

  if (!isAllowed) return <div className="text-center py-12 text-muted-foreground">Only Admin, Coordinator and Super Admin can use bulk task creation.</div>;

  return (
    <div className="space-y-6" data-testid="bulk-task-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Bulk Task Creation</h1>
        <p className="text-muted-foreground text-sm mt-1">Upload tasks via Excel or add multiple tasks at once</p>
      </div>

      <Tabs defaultValue="upload">
        <TabsList>
          <TabsTrigger value="upload" data-testid="tab-upload"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel Upload</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quick"><Table2 className="h-4 w-4 mr-1.5" />Quick Entry</TabsTrigger>
        </TabsList>

        {/* Excel Upload Tab */}
        <TabsContent value="upload" className="mt-4">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Upload Tasks from Excel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleTemplate} data-testid="download-template-btn">
                  <Download className="h-4 w-4 mr-2" />Download Template
                </Button>
                <label>
                  <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="file-upload-input" />
                  <Button asChild disabled={uploading}>
                    <span className="cursor-pointer">
                      {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {uploading ? 'Uploading...' : 'Upload Excel'}
                    </span>
                  </Button>
                </label>
              </div>

              {uploadResult && (
                <div className="mt-4 p-4 rounded-lg border border-border/50 bg-muted/30">
                  <p className="font-medium text-sm">{uploadResult.created} tasks created successfully</p>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-destructive font-medium">Errors:</p>
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-destructive">Row {err.row}: {err.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quick Entry Tab */}
        <TabsContent value="quick" className="mt-4">
          <Card className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Quick Task Entry</CardTitle>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="add-row-btn"><Plus className="h-4 w-4 mr-1" />Add Row</Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {isSuperAdmin && <th className="text-left py-2 px-2 font-medium text-muted-foreground">Department *</th>}
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground min-w-[280px]">Task Name *</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Due Date *</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Category</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Sub Category</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Priority *</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Assigned To *</th>
                      <th className="py-2 px-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const cats = categoriesByDept[row.department] || [];
                      const subs = subsByDeptCat[`${row.department}|${row.category}`] || [];
                      const filteredMembers = teamMembers.filter(m => !row.department || m.department === row.department);
                      return (
                      <tr key={i} className="border-b border-border/30">
                        {isSuperAdmin && (
                          <td className="py-2 px-2">
                            <Select
                              value={row.department || ''}
                              onValueChange={(v) => updateRow(i, 'department', v)}
                            >
                              <SelectTrigger className="h-9 w-44 text-sm" data-testid={`quick-dept-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                        )}
                        <td className="py-2 px-2 min-w-[280px]">
                          <Input value={row.task_name} onChange={(e) => updateRow(i, 'task_name', e.target.value)} placeholder="Task name" className="h-9 text-sm w-full min-w-[260px]" data-testid={`quick-name-${i}`} />
                        </td>
                        <td className="py-2 px-2">
                          <Input type="date" value={row.due_date} onChange={(e) => updateRow(i, 'due_date', e.target.value)} className="h-9 text-sm w-36" data-testid={`quick-date-${i}`} />
                        </td>
                        <td className="py-2 px-2">
                          <Select
                            value={row.category || 'none'}
                            onValueChange={(v) => updateRow(i, 'category', v === 'none' ? '' : v)}
                            disabled={!row.department}
                          >
                            <SelectTrigger className="h-9 w-44 text-sm" data-testid={`quick-category-${i}`}><SelectValue placeholder="Optional" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- None --</SelectItem>
                              {cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-2">
                          <Select
                            value={row.sub_category || 'none'}
                            onValueChange={(v) => updateRow(i, 'sub_category', v === 'none' ? '' : v)}
                            disabled={!row.category}
                          >
                            <SelectTrigger className="h-9 w-44 text-sm" data-testid={`quick-subcategory-${i}`}><SelectValue placeholder={row.category ? 'Optional' : 'Pick category first'} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- None --</SelectItem>
                              {subs.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-2">
                          <Select value={String(row.priority)} onValueChange={(v) => updateRow(i, 'priority', parseInt(v))}>
                            <SelectTrigger className="h-9 w-20 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>{Array.from({length:10},(_,j)=>j+1).map(p => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-2">
                          <Select value={row.assigned_to} onValueChange={(v) => updateRow(i, 'assigned_to', v)}>
                            <SelectTrigger className="h-9 text-sm w-44"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{filteredMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 px-2">
                          {rows.length > 1 && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRow(i)}><Trash2 className="h-4 w-4" /></Button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={handleQuickSubmit} disabled={quickSubmitting} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="quick-submit-btn">
                  {quickSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {quickSubmitting ? 'Creating...' : `Create ${rows.filter(r => r.task_name && r.due_date && r.assigned_to).length} Tasks`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkTaskPage;
